import React, { useState, useEffect, useMemo } from 'react';
import { Music } from 'lucide-react';
import api from '../utils/api';

/**
 * Tag-cloud widget. No dependencies — font-size scales linearly with frequency,
 * hue spins through a small palette so repeated colors cluster visually.
 * Source data is the pre-cleaned `topics` / `key_phrases` / `pros` / `cons`
 * arrays stored on tiktok_comment_analysis by the AI pipeline.
 */
const PALETTE = ['#60a5fa', '#34d399', '#f59e0b', '#c4b5fd', '#f472b6', '#22d3ee', '#fb7185'];

function pickColor(text) {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

const TikTokWordCloud = ({ accountId, limit = 60 }) => {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzedRows, setAnalyzedRows] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit });
        if (accountId) params.append('account_id', accountId);
        const res = await api.get(`/api/tiktok/wordcloud?${params}`);
        if (!cancel && res.data?.success) {
          setWords(res.data.words || []);
          setAnalyzedRows(res.data.analyzed_rows || 0);
        }
      } catch (e) {
        if (!cancel) console.error('wordcloud fetch failed', e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [accountId, limit]);

  const sized = useMemo(() => {
    if (!words.length) return [];
    const max = words[0].count;
    const min = words[words.length - 1].count;
    const range = Math.max(1, max - min);
    return words.map(w => ({
      ...w,
      // Font size ramps from 0.8rem (rare) to 2.4rem (most mentioned)
      fontSize: 0.8 + ((w.count - min) / range) * 1.6,
      color: pickColor(w.text.toLowerCase())
    }));
  }, [words]);

  return (
    <div className="dash-panel dash-wordcloud-panel">
      <div className="dash-panel-head">
        <div className="dash-panel-title">
          <Music size={18} color="#c4b5fd" /> TikTok buzzwords
        </div>
        <div className="dash-panel-count">
          {loading ? '…' : `${words.length} terms · ${analyzedRows} comments`}
        </div>
      </div>

      {loading && words.length === 0 ? (
        <div className="dash-empty" style={{ padding: '2rem' }}>Loading…</div>
      ) : words.length === 0 ? (
        <div className="dash-empty">
          <Music size={36} />
          <div>No analyzed TikTok comments yet. Run a scrape from the TikTok page and let the analyzer finish.</div>
        </div>
      ) : (
        <div className="dash-wordcloud">
          {sized.map(w => (
            <span
              key={w.text}
              className="dash-wordcloud-word"
              style={{ fontSize: `${w.fontSize}rem`, color: w.color }}
              title={`${w.text} — ${w.count} mentions`}
            >
              {w.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default TikTokWordCloud;
